import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  AssessmentFixture,
  AssessmentFixtureEvalSummary,
  AssessmentFixtureRun,
  AssessmentFixtureWithRuns,
  AssessmentRunStatus,
  Page,
} from "@/types";

/**
 * React Query hooks for the admin Assessment Fixtures feature. These wrap the
 * existing API client methods — the client is unchanged — and own the query
 * keys, caching, invalidation-on-success, and run polling (via
 * `refetchInterval`) that previously lived as manual `useEffect` loaders and a
 * `pollAssessmentRunStatus` helper. Follows the Sources slice template.
 */

export interface AssessmentFixturesListParams {
  page: number;
  page_size: number;
  sort_by?: "name" | "topic_title" | "created_at" | "updated_at";
  sort_order?: "asc" | "desc";
  q?: string;
}

export const assessmentFixtureKeys = {
  all: ["admin", "assessmentFixtures"] as const,
  lists: () => [...assessmentFixtureKeys.all, "list"] as const,
  list: (params: AssessmentFixturesListParams) =>
    [...assessmentFixtureKeys.lists(), params] as const,
  detail: (id: string) => [...assessmentFixtureKeys.all, "detail", id] as const,
  evalSummary: (id: string) => [...assessmentFixtureKeys.all, "evalSummary", id] as const,
  run: (runId: string) => [...assessmentFixtureKeys.all, "run", runId] as const,
};

export function useAssessmentFixtures(params: AssessmentFixturesListParams) {
  return useQuery<Page<AssessmentFixture>>({
    queryKey: assessmentFixtureKeys.list(params),
    queryFn: () => api.listAssessmentFixtures(params),
  });
}

export function useAssessmentFixture(id: string | undefined) {
  return useQuery<AssessmentFixtureWithRuns>({
    queryKey: assessmentFixtureKeys.detail(id ?? ""),
    queryFn: () => api.getAssessmentFixture(id as string),
    enabled: Boolean(id),
  });
}

export function useAssessmentFixtureEvalSummary(id: string | undefined) {
  return useQuery<AssessmentFixtureEvalSummary>({
    queryKey: assessmentFixtureKeys.evalSummary(id ?? ""),
    queryFn: () => api.getAssessmentFixtureEvalSummary(id as string),
    enabled: Boolean(id),
  });
}

export function useAssessmentFixtureRun(
  runId: string | undefined,
  options: { poll?: boolean } = {}
) {
  const { poll = false } = options;
  return useQuery<AssessmentFixtureRun>({
    queryKey: assessmentFixtureKeys.run(runId ?? ""),
    queryFn: () => api.getAssessmentFixtureRun(runId as string),
    enabled: Boolean(runId),
    refetchInterval: poll
      ? (query) => {
          const s = query.state.data?.status;
          return s === "completed" || s === "failed" ? false : 2000;
        }
      : undefined,
  });
}

/** Invalidate every Assessment Fixtures query (lists, details, runs) after a write. */
function useInvalidateAssessmentFixtures() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: assessmentFixtureKeys.all });
}

export function useCreateAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: ({
      payload,
      runImmediately,
    }: {
      payload: {
        name: string;
        topic_title: string;
        topic_description: string;
        article_pdf: File;
        notes?: string | null;
      };
      runImmediately?: boolean;
    }) => api.createAssessmentFixture(payload, runImmediately),
    onSuccess: invalidate,
  });
}

export function useUpdateAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{
        name: string;
        topic_title: string;
        topic_description: string;
        notes: string | null;
      }>;
    }) => api.updateAssessmentFixture(id, patch),
    onSuccess: invalidate,
  });
}

export function useReplaceAssessmentFixtureArticle() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.replaceAssessmentFixtureArticle(id, file),
    onSuccess: invalidate,
  });
}

export function useRunAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation<
    { run_id: string; status: AssessmentRunStatus },
    Error,
    { id: string; criterionKey?: string }
  >({
    mutationFn: ({ id, criterionKey }) => api.runAssessmentFixture(id, criterionKey),
    onSuccess: invalidate,
  });
}

export function useEvalSweepAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n?: number }) => api.evalSweepAssessmentFixture(id, n),
    onSuccess: invalidate,
  });
}

export function useCloneAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: (id: string) => api.cloneAssessmentFixture(id),
    onSuccess: invalidate,
  });
}

export function useDeleteAssessmentFixture() {
  const invalidate = useInvalidateAssessmentFixtures();
  return useMutation({
    mutationFn: (id: string) => api.deleteAssessmentFixture(id),
    onSuccess: invalidate,
  });
}
